import { useEffect, useState } from 'react';
import { getLinkQr } from '../../core/api/linksApi';
import { readLinkError, retryDelaySeconds } from '../../core/api/linkErrors';
import { LINK_ERROR_CODE as E, QR_FORMAT } from '../../core/constants/enums';

/**
 * Real QR thumbnails for the print preview (U63):
 * - S5 `format=png&size_px=128&ecc=<sel>`, no label and no download, so the
 *   response is cache-friendly (A07);
 * - cached by `(code, ecc)` in an LRU of `THUMB_CACHE_MAX` (200) entries;
 *   evicted object URLs are revoked, and `resetThumbnailCache` (called on
 *   logout from `MyContext`) revokes them all so a user switch never reuses
 *   the previous user's thumbnails;
 * - at most `MAX_IN_FLIGHT` (2) requests at once, so a preview never takes
 *   more than half of the 4-slot render cap (A65);
 * - `RENDER_BUSY` / `RATE_LIMITED` retry once after `Retry-After` (2 s
 *   fallback), then stay a placeholder. Thumbnails never show an error.
 */
export const THUMB_SIZE_PX = 128;
export const MAX_IN_FLIGHT = 2;
export const THUMB_CACHE_MAX = 200;
export const THUMB_STATE = Object.freeze({ READY: 'ready', FAILED: 'failed' });

/** Insertion order is recency order: the first key is least recently used. */
const cache = new Map();
const pending = new Map();
const queue = [];
let active = 0;
/** Bumped by a reset so work started before it cannot touch the new cache. */
let generation = 0;

const keyOf = (code, ecc) => `${code}|${ecc}`;
const RETRYABLE = new Set([E.RENDER_BUSY, E.RATE_LIMITED]);
const defaultWait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const FAILED_ENTRY = Object.freeze({ state: THUMB_STATE.FAILED, url: null });

function revoke(entry) {
  if (entry?.url && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
    URL.revokeObjectURL(entry.url);
  }
}

/** Read and mark as most recently used. */
function touch(key) {
  const entry = cache.get(key);
  cache.delete(key);
  cache.set(key, entry);
  return entry;
}

function store(key, entry) {
  if (cache.has(key)) revoke(cache.get(key));
  cache.delete(key);
  cache.set(key, entry);
  while (cache.size > THUMB_CACHE_MAX) {
    const [oldestKey, oldest] = cache.entries().next().value;
    cache.delete(oldestKey);
    revoke(oldest);
  }
}

function onTaskDone(startedIn) {
  // A reset already zeroed `active` for the old generation.
  if (startedIn !== generation) return;
  active -= 1;
  // eslint-disable-next-line no-use-before-define
  pump();
}

function pump() {
  while (active < MAX_IN_FLIGHT && queue.length) {
    const task = queue.shift();
    active += 1;
    task().finally(onTaskDone.bind(null, generation));
  }
}

const toUrl = (blob) =>
  typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function' && blob ? URL.createObjectURL(blob) : null;

async function fetchThumb(code, ecc, { request, wait }) {
  const params = { format: QR_FORMAT.PNG, size_px: THUMB_SIZE_PX, ecc, label: false, download: false };
  try {
    return await request(code, params);
  } catch (error) {
    const parsed = readLinkError(error);
    if (!RETRYABLE.has(parsed.code)) throw error;
    await wait(retryDelaySeconds(parsed) * 1000);
    return request(code, params);
  }
}

/**
 * Load one thumbnail into the cache (deduplicated, queued).
 * @returns {Promise<{ state: string, url: string|null }>}
 */
export function loadThumbnail(code, ecc, { request = getLinkQr, wait = defaultWait } = {}) {
  const key = keyOf(code, ecc);
  if (cache.has(key)) return Promise.resolve(touch(key));
  if (pending.has(key)) return pending.get(key);
  const startedIn = generation;
  const promise = new Promise((resolve) => {
    queue.push(async () => {
      let entry;
      try {
        const { blob } = await fetchThumb(code, ecc, { request, wait });
        const url = toUrl(blob);
        entry = url ? { state: THUMB_STATE.READY, url } : FAILED_ENTRY;
      } catch {
        entry = FAILED_ENTRY;
      }
      if (startedIn !== generation) {
        // Fetched for the previous session: drop it rather than leak or reuse it.
        revoke(entry);
        resolve(FAILED_ENTRY);
        return;
      }
      store(key, entry);
      pending.delete(key);
      resolve(entry);
    });
    pump();
  });
  pending.set(key, promise);
  return promise;
}

/** Test helper. */
export const thumbnailStats = () => ({ active, queued: queue.length });

/** Revoke every cached URL and forget all queued and in-flight work (logout). */
export function resetThumbnailCache() {
  generation += 1;
  cache.forEach(revoke);
  cache.clear();
  pending.clear();
  queue.length = 0;
  active = 0;
}

/**
 * @param {string[]} codes - Distinct codes on the visible page.
 * @param {string} ecc
 * @returns {(code: string) => { state: string, url: string|null }|null}
 */
export function useQrThumbnails(codes, ecc) {
  const [, setTick] = useState(0);
  const codesKey = codes.join(',');

  useEffect(() => {
    let isAlive = true;
    codesKey
      .split(',')
      .filter(Boolean)
      .forEach((code) => {
        loadThumbnail(code, ecc).then(() => {
          if (isAlive) setTick((n) => n + 1);
        });
      });
    return () => {
      isAlive = false;
    };
  }, [codesKey, ecc]);

  return (code) => cache.get(keyOf(code, ecc)) || null;
}
